import type { ReactNode } from "react";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Avatar } from "@/components/ui/Avatar";
import type { AvatarUser } from "@/components/ui/Avatar";
import type { ViewStatusOption } from "@/lib/boardView";
import { statusColorOf } from "@/lib/tokens";
import { t } from "@/lib/strings";

interface DetailFieldProps {
  icon: IconName;
  label: string;
  children: ReactNode;
  last?: boolean;
}

export function DetailField({ icon, label, children, last = false }: DetailFieldProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        minHeight: 40,
        padding: "6px 14px",
        borderBottom: last ? "none" : "1px solid var(--hairline-soft)",
        gap: 12,
      }}
    >
      <Icon name={icon} size={14} color="var(--ink-3)" />
      <span
        style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 500, width: 100, flexShrink: 0 }}
      >
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

interface ActivityItemProps {
  user: AvatarUser | null | undefined;
  when: string;
  action: ReactNode;
}

export function ActivityItem({ user, when, action }: ActivityItemProps) {
  if (!user) return null;
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <Avatar user={user} size={22} />
      <div style={{ flex: 1, fontSize: 13, lineHeight: 1.5, color: "var(--ink-2)" }}>
        <strong style={{ color: "var(--ink-1)", fontWeight: 500 }}>{user.display_name}</strong>{" "}
        {action}
      </div>
      <span style={{ fontSize: 11.5, color: "var(--ink-4)", flexShrink: 0 }}>{when}</span>
    </div>
  );
}

interface StatusCellInlineProps {
  options: ViewStatusOption[];
  value: string | null;
  onChange: (id: string) => void;
  /** Display-only: render just the active option as a non-interactive pill.
   * Used on ticket boards where status only moves through workflow actions. */
  readOnly?: boolean;
}

export function StatusCellInline({
  options,
  value,
  onChange,
  readOnly = false,
}: StatusCellInlineProps) {
  if (readOnly) {
    const active = options.find((o) => o.id === value);
    if (!active) {
      return <span style={{ color: "var(--ink-4)", fontSize: 13.5 }}>—</span>;
    }
    const tint = statusColorOf(active.color);
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 10px",
          borderRadius: 999,
          background: tint.bg,
          color: tint.fg,
          fontSize: 12,
          fontWeight: 500,
          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        }}
      >
        {active.label}
      </span>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => {
        const active = o.id === value;
        const tint = statusColorOf(o.color);
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              border: "none",
              background: active ? tint.bg : "transparent",
              color: active ? tint.fg : "var(--ink-3)",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: active ? "none" : `inset 0 0 0 1px var(--hairline)`,
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

interface PersonFieldProps {
  owners: AvatarUser[];
}

export function PersonField({ owners }: PersonFieldProps) {
  if (owners.length === 0) {
    return <span style={{ color: "var(--ink-4)", fontSize: 13.5 }}>+ {t.assign}</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {owners.map((u) => (
        <span
          key={u.id}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 10px 3px 3px",
            borderRadius: 999,
            background: "var(--surface)",
            boxShadow: `inset 0 0 0 1px var(--hairline)`,
            fontSize: 12.5,
            color: "var(--ink-1)",
          }}
        >
          <Avatar user={u} size={20} />
          {u.display_name}
        </span>
      ))}
      <button
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 10px",
          borderRadius: 999,
          border: "none",
          background: "transparent",
          color: "var(--ink-3)",
          boxShadow: `inset 0 0 0 1px var(--hairline)`,
          fontSize: 12.5,
          cursor: "pointer",
          fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        }}
      >
        <Icon name="plus" size={12} /> {t.addPerson}
      </button>
    </div>
  );
}
